import { useEffect, useMemo, useCallback } from "react";
import { addDays, format, isBefore, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, Download, HelpCircle, RefreshCw, X } from "lucide-react";
import { formatCurrency, formatCurrencyWithCents } from "@/lib/currency";
import {
  usePaymentRuns,
  usePaymentRunInvoices,
  type PaymentRun,
  type PaymentRunInvoice,
  type NewRunInvoice,
} from "@/hooks/usePaymentRuns";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";

const statusColors: Record<PaymentRun["status"], string> = {
  draft: "bg-muted/60 text-muted-foreground border-border",
  processing: "bg-warning/10 text-warning border-warning/20",
  completed: "bg-primary/10 text-primary border-primary/20",
};

interface PaymentRunDetailModalProps {
  run: PaymentRun | null;
  open: boolean;
  onClose: () => void;
  allInvoices?: PARInvoiceRow[];
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
}

/* ---------- Greedy knapsack (same algorithm as OptimizeTab) ---------- */
interface EligibleRow {
  vendor_name: string;
  invoice_number: string | null;
  outstanding: number;
  potential_savings: number;
  savingsRatio: number;
  early_pay_due_date: string;
}

function buildEligible(invoices: PARInvoiceRow[], payDate: string): EligibleRow[] {
  const groups = new Map<string, { rows: PARInvoiceRow[] }>();
  for (const inv of invoices) {
    const outstanding = inv.amount_invoiced - inv.amount_paid;
    if (outstanding <= 0) continue;
    if (!inv.discount_pct || inv.discount_pct <= 0) continue;
    if (inv.potential_savings <= 0) continue;
    if (!inv.early_pay_due_date || inv.early_pay_due_date < payDate) continue;
    const key = `${inv.vendor_name}|${inv.invoice_number ?? ""}`;
    if (!groups.has(key)) groups.set(key, { rows: [] });
    groups.get(key)!.rows.push(inv);
  }
  const result: EligibleRow[] = [];
  for (const { rows } of groups.values()) {
    const totalOutstanding = rows.reduce((s, r) => s + (r.amount_invoiced - r.amount_paid), 0);
    const totalSavings = rows.reduce((s, r) => s + r.potential_savings, 0);
    const earlyDates = rows
      .map((r) => r.early_pay_due_date)
      .filter(Boolean)
      .sort() as string[];
    const minEarlyDate = earlyDates[0];
    if (!minEarlyDate) continue;
    result.push({
      vendor_name: rows[0].vendor_name,
      invoice_number: rows[0].invoice_number ?? null,
      outstanding: totalOutstanding,
      potential_savings: totalSavings,
      savingsRatio: totalOutstanding > 0 ? totalSavings / totalOutstanding : 0,
      early_pay_due_date: minEarlyDate,
    });
  }
  return result.sort((a, b) => b.savingsRatio - a.savingsRatio);
}

function greedySelect(eligible: EligibleRow[], budget: number): EligibleRow[] {
  const selected: EligibleRow[] = [];
  let remaining = budget;
  for (const inv of eligible) {
    if (inv.outstanding <= remaining) {
      selected.push(inv);
      remaining -= inv.outstanding;
    }
  }
  return selected;
}

function exportRunAsCSV(run: PaymentRun, invoices: PaymentRunInvoice[]) {
  const headers = ["Vendor", "Invoice #", "Amount", "Early Pay Due Date", "Potential Savings"];
  const rows = invoices.map((inv) => [
    `"${inv.vendor_name.replace(/"/g, '""')}"`,
    `"${(inv.invoice_number ?? "").replace(/"/g, '""')}"`,
    inv.outstanding.toFixed(2),
    inv.early_pay_due_date ?? "",
    inv.potential_savings.toFixed(2),
  ]);
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payment-run-${run.pay_date}.csv`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/* ---------- Past-run week invoice type ---------- */
interface WeekInvoice {
  key: string;
  vendor_name: string;
  invoice_number: string | null;
  amount: number;
  early_pay_due_date: string;
  paid_date: string | null;
  potential_savings: number;
  status: "paid_early" | "paid_late" | "unpaid";
}

export function PaymentRunDetailModal({
  run,
  open,
  onClose,
  allInvoices = [],
}: PaymentRunDetailModalProps) {
  const { updateRun } = usePaymentRuns();
  const { toast } = useToast();
  const {
    invoices,
    loading: invoicesLoading,
    fetchInvoices,
    updateInvoiceInclusion: _updateInvoiceInclusion,
    replaceInvoices: _replaceInvoices,
  } = usePaymentRunInvoices(run?.id ?? null);

  useEffect(() => {
    if (open && run) {
      fetchInvoices();
    }
  }, [open, run, fetchInvoices]);

  const isPast = useMemo(() => {
    if (!run) return false;
    try {
      const sunday = addDays(parseISO(run.pay_date + "T00:00:00"), 6);
      return isBefore(sunday, new Date());
    } catch {
      return false;
    }
  }, [run]);

  const includedInvoices = useMemo(() => invoices.filter((i) => i.included), [invoices]);

  /* ---------- weekInvoices: all allInvoices rows whose early_pay_due_date falls in the run's week ---------- */
  const weekInvoices = useMemo((): WeekInvoice[] => {
    if (!run || !isPast || allInvoices.length === 0) return [];
    const weekStart = run.pay_date;
    const weekEnd = format(addDays(parseISO(run.pay_date + "T00:00:00"), 6), "yyyy-MM-dd");

    const deduped = new Map<string, WeekInvoice>();
    for (const r of allInvoices) {
      if (!r.early_pay_due_date) continue;
      if (r.early_pay_due_date < weekStart || r.early_pay_due_date > weekEnd) continue;
      const key = `${r.vendor_name}|${r.invoice_number ?? ""}|${r.early_pay_due_date}`;
      const paidDate = r.paid_date || null;
      const status: WeekInvoice["status"] =
        paidDate && paidDate <= r.early_pay_due_date
          ? "paid_early"
          : paidDate
            ? "paid_late"
            : "unpaid";
      const existing = deduped.get(key);
      const rank = (s: WeekInvoice["status"]) =>
        s === "paid_early" ? 2 : s === "paid_late" ? 1 : 0;
      if (!existing || rank(status) > rank(existing.status)) {
        deduped.set(key, {
          key,
          vendor_name: r.vendor_name,
          invoice_number: r.invoice_number ?? null,
          amount: r.amount_invoiced,
          early_pay_due_date: r.early_pay_due_date,
          paid_date: paidDate,
          potential_savings: r.potential_savings ?? 0,
          status,
        });
      }
    }
    return Array.from(deduped.values()).sort((a, b) => {
      const rank = (s: WeekInvoice["status"]) =>
        s === "paid_early" ? 0 : s === "paid_late" ? 1 : 2;
      if (rank(a.status) !== rank(b.status)) return rank(a.status) - rank(b.status);
      return b.amount - a.amount;
    });
  }, [run, isPast, allInvoices]);

  const weekPaidEarly = useMemo(
    () => weekInvoices.filter((i) => i.status === "paid_early"),
    [weekInvoices]
  );
  const weekPaidLate = useMemo(
    () => weekInvoices.filter((i) => i.status === "paid_late"),
    [weekInvoices]
  );
  const weekUnpaid = useMemo(
    () => weekInvoices.filter((i) => i.status === "unpaid"),
    [weekInvoices]
  );
  const weekCapturedSavings = useMemo(
    () => weekPaidEarly.reduce((s, i) => s + i.potential_savings, 0),
    [weekPaidEarly]
  );
  const weekCaptureRate = useMemo(
    () => (weekInvoices.length > 0 ? weekPaidEarly.length / weekInvoices.length : null),
    [weekInvoices, weekPaidEarly]
  );

  const hasNewInvoices = useMemo(() => {
    if (!run || allInvoices.length === 0 || invoices.length === 0) return false;
    const eligible = buildEligible(allInvoices, run.pay_date);
    const existingKeys = new Set(invoices.map((i) => `${i.vendor_name}|${i.invoice_number ?? ""}`));
    return eligible.some((e) => !existingKeys.has(`${e.vendor_name}|${e.invoice_number ?? ""}`));
  }, [allInvoices, invoices, run]);
  const totalAmount = useMemo(
    () => includedInvoices.reduce((s, i) => s + i.outstanding, 0),
    [includedInvoices]
  );
  const totalSavings = useMemo(
    () => includedInvoices.reduce((s, i) => s + i.potential_savings, 0),
    [includedInvoices]
  );

  const showSaving = useCallback(() => {
    toast({ description: "Saving…", duration: 1200 });
  }, [toast]);

  const updateInvoiceInclusion = useCallback(
    async (id: string, included: boolean) => {
      showSaving();
      await _updateInvoiceInclusion(id, included);
    },
    [_updateInvoiceInclusion, showSaving]
  );

  const replaceInvoices = useCallback(
    async (newInvoices: NewRunInvoice[], eligibleSavings?: number) => {
      showSaving();
      await _replaceInvoices(newInvoices, eligibleSavings);
    },
    [_replaceInvoices, showSaving]
  );

  const handleMarkComplete = async () => {
    if (!run) return;
    showSaving();
    await updateRun(run.id, { status: "completed" });
  };

  const handleReoptimize = async () => {
    if (!run) return;
    const eligible = buildEligible(allInvoices, run.pay_date);
    const selected = greedySelect(eligible, run.budget);
    const newInvoices: NewRunInvoice[] = selected.map((r) => ({
      vendor_name: r.vendor_name,
      invoice_number: r.invoice_number,
      outstanding: r.outstanding,
      potential_savings: r.potential_savings,
      early_pay_due_date: r.early_pay_due_date,
    }));
    const eligibleSavings = eligible.reduce((s, r) => s + r.potential_savings, 0);
    await replaceInvoices(newInvoices, eligibleSavings);
  };

  if (!run) return null;

  /* ---------- Status badge colors for week invoice rows ---------- */
  const weekStatusBadge: Record<WeekInvoice["status"], string> = {
    paid_early: "bg-success/10 text-success border-success/20",
    paid_late: "bg-destructive/10 text-destructive border-destructive/20",
    unpaid: "bg-muted/60 text-muted-foreground border-border",
  };
  const weekStatusLabel: Record<WeekInvoice["status"], string> = {
    paid_early: "Paid Early",
    paid_late: "Not Paid Early",
    unpaid: "Unknown / Pending",
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <DialogTitle className="text-base">{run.name}</DialogTitle>
            <Badge className={cn("shrink-0 text-[10px] border", statusColors[run.status])}>
              {run.status}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Pay date {fmtDate(run.pay_date)} · Budget {formatCurrency(run.budget)} · Created{" "}
            {fmtDate(run.created_at)}
          </p>
        </DialogHeader>

        {/* Summary metrics */}
        {isPast && allInvoices.length > 0 ? (
          <div className="shrink-0 space-y-2">
            {/* Status count pills */}
            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="flex items-center gap-1 rounded-full bg-success/10 text-success border border-success/20 px-2.5 py-0.5 font-semibold">
                <Check className="w-3 h-3" />
                {weekPaidEarly.length} paid early ·{" "}
                {formatCurrency(weekPaidEarly.reduce((s, i) => s + i.amount, 0))}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-destructive/10 text-destructive border border-destructive/20 px-2.5 py-0.5 font-semibold">
                <X className="w-3 h-3" />
                {weekPaidLate.length} not paid early ·{" "}
                {formatCurrency(weekPaidLate.reduce((s, i) => s + i.amount, 0))}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-muted/60 text-muted-foreground border border-border px-2.5 py-0.5 font-semibold">
                <HelpCircle className="w-3 h-3" />
                {weekUnpaid.length} unknown / pending ·{" "}
                {formatCurrency(weekUnpaid.reduce((s, i) => s + i.amount, 0))}
              </span>
            </div>
            {/* Metric cards */}
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg border border-success/30 bg-success/5 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                  Savings Captured
                </p>
                <p className="text-lg font-bold text-success">
                  {formatCurrencyWithCents(weekCapturedSavings)}
                </p>
              </div>
              <div className="flex-1 rounded-lg border px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                  Capture Rate
                </p>
                <p className="text-lg font-bold">
                  {weekCaptureRate !== null ? `${Math.round(weekCaptureRate * 100)}%` : "—"}
                  <span className="text-xs font-normal text-muted-foreground ml-1">
                    ({weekPaidEarly.length}/{weekInvoices.length})
                  </span>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 shrink-0">
            <div className="flex-1 rounded-lg border bg-muted/30 px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Total Amount
              </p>
              <p className="text-lg font-bold">{formatCurrency(totalAmount)}</p>
            </div>
            <div className="flex-1 rounded-lg border border-success/30 bg-success/5 px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Total Savings
              </p>
              <p className="text-lg font-bold text-success">
                {formatCurrencyWithCents(totalSavings)}
              </p>
            </div>
            <div className="flex-1 rounded-lg border px-3 py-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Invoices
              </p>
              <p className="text-lg font-bold">
                {includedInvoices.length}
                {invoices.length !== includedInvoices.length && (
                  <span className="text-xs font-normal text-muted-foreground ml-1">
                    / {invoices.length}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Invoice table */}
        <div className="flex-1 overflow-auto border rounded-md min-h-0">
          {isPast && allInvoices.length > 0 ? (
            /* Past run: all invoices with early_pay_due_date in the week, with status */
            <Table className="table-fixed">
              <colgroup>
                <col style={{ width: "24%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="[&_th]:py-1.5 [&_th]:px-2 [&_th]:text-[11px] [&_th]:font-semibold">
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Early Pay Due</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weekInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      No invoices with early pay deadline this week
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {weekPaidEarly.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-success"
                          >
                            <Check className="w-3 h-3 inline-block mr-1" />
                            Paid Early ({weekPaidEarly.length})
                          </TableCell>
                        </TableRow>
                        {weekPaidEarly.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 bg-success/5">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-success font-medium">
                              {fmtDate(inv.paid_date)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {weekPaidLate.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-destructive"
                          >
                            <X className="w-3 h-3 inline-block mr-1" />
                            Not Paid Early ({weekPaidLate.length})
                          </TableCell>
                        </TableRow>
                        {weekPaidLate.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 opacity-80">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-destructive font-medium">
                              {fmtDate(inv.paid_date)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {weekUnpaid.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-muted-foreground"
                          >
                            <HelpCircle className="w-3 h-3 inline-block mr-1" />
                            Unknown / Pending ({weekUnpaid.length})
                          </TableCell>
                        </TableRow>
                        {weekUnpaid.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 opacity-60">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">—</TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          ) : (
            /* Non-past run (or no PAR data): original editable table */
            <Table className="table-fixed">
              <colgroup>
                <col style={{ width: "36px" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="[&_th]:py-1.5 [&_th]:px-2 [&_th]:text-[11px] [&_th]:font-semibold">
                  <TableHead />
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Early Pay Due</TableHead>
                  <TableHead className="text-right">Savings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoicesLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      No invoices in this run
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow
                      key={inv.id}
                      className={cn(
                        "[&_td]:py-1 [&_td]:px-2 cursor-pointer transition-colors",
                        inv.included ? "bg-success/5 border-l-2 border-l-success" : "opacity-50"
                      )}
                      onClick={() => updateInvoiceInclusion(inv.id, !inv.included)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={inv.included}
                          onCheckedChange={(checked) =>
                            updateInvoiceInclusion(inv.id, checked === true)
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="h-3.5 w-3.5"
                        />
                      </TableCell>
                      <TableCell
                        className="text-[11px] font-medium truncate"
                        title={inv.vendor_name}
                      >
                        {inv.vendor_name}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-mono">
                        {inv.invoice_number || "—"}
                      </TableCell>
                      <TableCell className="text-[11px] font-semibold text-right">
                        {formatCurrency(inv.outstanding)}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">
                        {fmtDate(inv.early_pay_due_date)}
                      </TableCell>
                      <TableCell className="text-[11px] font-bold text-success text-right">
                        {formatCurrencyWithCents(inv.potential_savings)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 flex items-center justify-between border-t pt-3">
          <div className="flex items-center gap-2">
            {!isPast && (
              <Button
                size="sm"
                variant="outline"
                className={cn("gap-1.5 text-xs", hasNewInvoices && "border-warning/50")}
                onClick={handleReoptimize}
                disabled={allInvoices.length === 0}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {hasNewInvoices ? "Re-optimize (new invoices)" : "Re-optimize"}
                {hasNewInvoices && (
                  <span className="ml-0.5 w-2 h-2 rounded-full bg-warning shrink-0" />
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={() => exportRunAsCSV(run, invoices)}
              disabled={invoices.length === 0}
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </Button>
          </div>
          {run.status !== "completed" && !isPast && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={handleMarkComplete}
            >
              <Check className="w-3.5 h-3.5" />
              Mark Complete
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
